import { Fragment, useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router";

import {
  BuilderContext,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import { useURL } from "~/contexts";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/hec/components/ui/breadcrumb";
import { useParentGoalTitle } from "~/hooks/learning-center/use-parent-goal-title";

import messages from "./messages";
import {
  type GoalData,
  type LearningCenterPageType,
  type ModuleData,
  type TrailLabels,
  buildLearningCenterTrail,
  parseLevelModule,
} from "./trail";

/**
 * SFCC-11364 (ARTICLES-13, deferred half) — port of hec-composable's goal/module
 * `LearningCenterBreadcrumb` Builder block (the sibling of the article breadcrumb
 * shipped in SFCC-11240). Renders the breadcrumb trail on the HSA Learning Center
 * goal/module pages (`/learning-center/<goal>[/<module>]`, the
 * `learning-center-page` model), registered in `customComponents` and placed via
 * Builder on the 37 live `learning-center-page` entries.
 *
 * Page type comes from the entry's `data.pageType` (`goal` / `module` / `default`),
 * read off `BuilderContext.content.data` — the same `@builder.io/sdk-react` v4 API
 * the composable's `useLearningCenterPage` uses (both repos on 4.2.x). The
 * goal/module page route (`$.tsx`) forces `includeRefs=true`, inlining
 * `data.goal.value.data` (title, dashboard) and `data.module.value.data` (id,
 * page) — exactly the fields the trail reads. No client fetch.
 *
 * Trails (see `buildLearningCenterTrail`):
 *  - goal    → `Home › HSA Learning Center › <goal title>`
 *  - module  → `Home › HSA Learning Center › Goal: <title> › Level N: Module M`
 *  - default → `Home › HSA Learning Center › <page title>`
 *
 * MODULE-page goal title (SFCC-11727): a module page inlines only `data.module`,
 * not `data.goal`, so the parent-goal TITLE for the `Goal: <title>` crumb is not
 * resolvable from `BuilderContext` alone. We resolve it with a client-side reverse
 * fetch — module → parent level → parent goal — via `useParentGoalTitle`, mirroring
 * the composable's `useFetchParentLevel` → `useFetchParentGoal` chain and following
 * the client Builder-fetch convention shipped for the GoalTile dashboard ref
 * (SFCC-11732: `fetchOneEntry`, `userAttributes`, `AbortSignal.timeout`). The goal
 * `href` always resolves from the module id's goal slug, so only the title needs the
 * fetch. On a miss/timeout the title is `undefined` and the trail falls back to the
 * current module-only subset (`Home › HSA Learning Center › Level N: Module M`) — no
 * broken trail. The goal-page trail is unchanged (goal title is inlined there).
 *
 * Labels/links are HSA-hardcoded, matching the composable (Learning Center is
 * HSA-only). On FSA/WDH this block isn't placed on any entry, so it renders nothing.
 *
 * Styling/markup mirror the shipped `LearningCenterArticleBreadcrumb`.
 */

interface BuilderPageData {
  pageType?: unknown;
  title?: unknown;
  goal?: { value?: { data?: GoalData | null } | null } | null;
  // `module.id` is the module entry's Builder content id (the reference id),
  // used as the reverse-lookup key for the parent-goal title fetch (SFCC-11727).
  // `module.value.data` is the inlined payload (id slug, page ref).
  module?: {
    id?: string;
    value?: { data?: ModuleData | null } | null;
  } | null;
}

const VALID_PAGE_TYPES: readonly LearningCenterPageType[] = [
  "goal",
  "module",
  "default",
];

function asPageType(value: unknown): LearningCenterPageType | undefined {
  return typeof value === "string" &&
    (VALID_PAGE_TYPES as readonly string[]).includes(value)
    ? (value as LearningCenterPageType)
    : undefined;
}

const LearningCenterBreadcrumb = ({
  separator = ">",
  attributes,
}: {
  separator?: string;
  attributes?: Record<string, unknown> & { className?: string };
}) => {
  const url = useURL();
  const intl = useIntl();
  const builderContext = useContext(BuilderContext);

  // Read page data off BuilderContext. Every deref is guarded — the block can be
  // dropped on the Builder editor canvas (no page context) or on a partially
  // resolved entry, and must never throw.
  const pageData = builderContext?.content?.data as BuilderPageData | undefined;

  const pageType = asPageType(pageData?.pageType);
  // `goal` / `module` references are inlined under `includeRefs` (route forces it);
  // `?.value?.data` is the inlined payload.
  const goal = pageData?.goal?.value?.data ?? undefined;
  const module = pageData?.module?.value?.data ?? undefined;
  // Harden `title` against a non-string value (e.g. a rich-text object) so we
  // never pass a non-renderable value through to `{crumb.label}`.
  const title =
    typeof pageData?.title === "string" ? pageData.title : undefined;

  // SFCC-11727 — resolve the parent-goal title for the module-page `Goal: <title>`
  // crumb via a client-side reverse fetch (module → parent level → parent goal).
  // A module page inlines only `data.module`, never `data.goal`, so the title is
  // missing from BuilderContext. Gate the fetch to module pages whose goal title
  // isn't already inlined (defensive — module entries never inline it today); the
  // hook is a no-op otherwise. Key is the module entry's content id (`module.id`).
  const moduleContentId = pageData?.module?.id;
  const needsParentGoalTitle = pageType === "module" && !goal?.title;
  const { title: resolvedGoalTitle } = useParentGoalTitle(
    moduleContentId,
    needsParentGoalTitle,
  );

  // Merge the reverse-fetched title onto the (otherwise un-inlined) goal so the
  // trail-builder can emit the goal crumb. Falls back to the inlined goal as-is
  // on a miss/timeout (`resolvedGoalTitle` undefined → trail omits the crumb).
  const goalForTrail = useMemo<GoalData | undefined>(() => {
    if (!needsParentGoalTitle) return goal;
    if (!resolvedGoalTitle) return goal;
    return { ...(goal ?? {}), title: resolvedGoalTitle };
  }, [goal, needsParentGoalTitle, resolvedGoalTitle]);

  // SFCC-11723 — localized crumb labels formatted here (react-intl) and threaded
  // into the framework-free trail builder. `goalLabel`/`levelModuleLabel` are
  // pre-interpolated with the resolved goal title and the parsed level/module.
  // (This `labels` arg was dropped in the SFCC-11723↔11727 merge — SFCC-11733-adjacent.)
  const { level, module: moduleNumber } = parseLevelModule(module?.id);
  const labels = useMemo<TrailLabels>(
    () => ({
      home: intl.formatMessage(messages.home),
      learningCenter: intl.formatMessage(messages.learningCenter),
      goalLabel: intl.formatMessage(messages.goalLabel, {
        title: goalForTrail?.title ?? "",
      }),
      levelModuleLabel: intl.formatMessage(messages.levelModuleLabel, {
        level: level ?? "",
        module: moduleNumber ?? "",
      }),
    }),
    [intl, goalForTrail?.title, level, moduleNumber],
  );

  const crumbs = useMemo(
    () =>
      buildLearningCenterTrail({
        pageType,
        goal: goalForTrail,
        module,
        title,
        labels,
      }),
    [pageType, goalForTrail, module, title, labels],
  );

  if (crumbs.length === 0) return null;

  const { className, ...rest } = attributes ?? {};

  return (
    <Breadcrumb
      className={className}
      data-testid="learningCenterBreadcrumb"
      {...rest}
    >
      <BreadcrumbList
        data-testid="lc-breadcrumb-list"
        className="font-grotesque text-sm font-light leading-5.5 text-blue-03"
      >
        {crumbs.map((crumb, i) => (
          <Fragment key={`${crumb.label}-${i}`}>
            <BreadcrumbItem data-testid="lc-breadcrumb-item">
              {crumb.href ? (
                <Link
                  to={url(crumb.href)}
                  prefetch="intent"
                  className="font-light text-blue-03 underline hover:no-underline"
                >
                  {crumb.label}
                </Link>
              ) : (
                <BreadcrumbPage
                  data-testid="lc-breadcrumb-current"
                  className="font-light text-blue-04"
                >
                  {crumb.label}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
            {i < crumbs.length - 1 && (
              <BreadcrumbSeparator
                data-testid="lc-breadcrumb-separator"
                className="text-blue-03"
              >
                {separator}
              </BreadcrumbSeparator>
            )}
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export const LearningCenterBreadcrumbDefinition = {
  component: LearningCenterBreadcrumb,
  name: "LearningCenterBreadcrumb",
  noWrap: true,
  canHaveChildren: false,
  inputs: [
    {
      name: "separator",
      type: "string",
      defaultValue: ">",
      friendlyName: "Breadcrumb Separator",
      helperText:
        "Character shown between crumbs. Only renders on a live goal/module page — the trail is empty in the Builder editor canvas (no page context).",
    },
  ],
} satisfies RegisteredComponent;

export default LearningCenterBreadcrumb;
